/**
 * Ticket API Service
 *
 * REST client for /api/tickets — CRUD and CAB-driven generation.
 */

import api from '../utils/axios';
import type { Ticket, TicketWorkType, TicketStatus, TicketPriority, TicketSource, TicketExpertise, TicketPlatformType } from '../pages/TicketsPage';

const BASE = '/tickets';

// ─── API shapes (snake_case from backend) ────────────────────────────────────

export interface ApiAssignTo {
  name: string;
  email: string;
}

interface ApiTicketSummary {
  id: string;
  project_id: string;
  title: string;
  status: TicketStatus;
  priority: TicketPriority;
  source: TicketSource;
  work_type?: TicketWorkType;
  expertise: TicketExpertise;
  platform_type: TicketPlatformType;
  assign_to?: ApiAssignTo;
  due_date?: string;
  cab_reference?: string;
  created_date: string;
  created_by: string;
}

interface ApiTicket extends ApiTicketSummary {
  description: string;
}

// ─── Query / pagination types ─────────────────────────────────────────────────

export type SortField = 'id' | 'title' | 'status' | 'priority' | 'work_type' | 'source' | 'assign_to' | 'created_date';
export type SortDir = 'asc' | 'desc';
export const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
export type PageSize = typeof PAGE_SIZE_OPTIONS[number];

export interface TicketListParams {
  page: number;
  pageSize: PageSize;
  sortField: SortField;
  sortDir: SortDir;
  search?: string;
  status?: string[];
  priority?: string[];
  source?: string[];
  workType?: string[];
  view?: 'all' | 'my';
}

export interface TicketListResult {
  tickets: Ticket[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface CreateTicketRequest {
  title: string;
  description?: string;
  status?: TicketStatus;
  priority?: TicketPriority;
  source?: TicketSource;
  work_type?: TicketWorkType;
  expertise?: TicketExpertise;
  platform_type?: TicketPlatformType;
  assign_to?: ApiAssignTo;
  due_date?: string;
  cab_reference?: string;
}

export interface UpdateTicketRequest {
  title?: string;
  description?: string;
  status?: TicketStatus;
  priority?: TicketPriority;
  source?: TicketSource;
  work_type?: TicketWorkType;
  expertise?: TicketExpertise;
  platform_type?: TicketPlatformType;
  assign_to?: ApiAssignTo | null;
  due_date?: string;
  cab_reference?: string;
}

export interface GenerateFromCABResult {
  created: number;
  tickets: Ticket[];
}

// ─── Mappers ──────────────────────────────────────────────────────────────────

function mapSummary(t: ApiTicketSummary): Ticket {
  return {
    id: t.id,
    projectId: t.project_id,
    title: t.title,
    description: '',
    status: t.status,
    priority: t.priority,
    source: t.source,
    workType: t.work_type,
    expertise: t.expertise,
    platformType: t.platform_type,
    assignTo: t.assign_to ?? null,
    dueDate: t.due_date,
    cabReference: t.cab_reference,
    createdDate: t.created_date,
    createdBy: t.created_by,
  };
}

function mapTicket(t: ApiTicket): Ticket {
  return { ...mapSummary(t), description: t.description };
}

// ─── API functions ────────────────────────────────────────────────────────────

export async function listTickets(projectId: string, params: TicketListParams): Promise<TicketListResult> {
  const q = new URLSearchParams();
  q.set('page', String(params.page));
  q.set('page_size', String(params.pageSize));
  q.set('sort_field', params.sortField);
  q.set('sort_dir', params.sortDir);
  if (params.view) q.set('view', params.view);
  if (params.search) q.set('search', params.search);
  params.status?.forEach(v => q.append('status', v));
  params.priority?.forEach(v => q.append('priority', v));
  params.source?.forEach(v => q.append('source', v));
  params.workType?.forEach(v => q.append('work_type', v));

  const res = await api.get<{
    tickets: ApiTicketSummary[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
  }>(`${BASE}/${projectId}?${q.toString()}`);

  return {
    tickets: res.data.tickets.map(mapSummary),
    total: res.data.total,
    page: res.data.page,
    pageSize: res.data.page_size,
    totalPages: res.data.total_pages,
  };
}

export async function listTicketIds(projectId: string, params: Omit<TicketListParams, 'page' | 'pageSize'>): Promise<string[]> {
  const q = new URLSearchParams();
  q.set('sort_field', params.sortField);
  q.set('sort_dir', params.sortDir);
  if (params.view) q.set('view', params.view);
  if (params.search) q.set('search', params.search);
  params.status?.forEach(v => q.append('status', v));
  params.priority?.forEach(v => q.append('priority', v));
  params.source?.forEach(v => q.append('source', v));
  params.workType?.forEach(v => q.append('work_type', v));

  const res = await api.get<string[]>(`${BASE}/${projectId}/ids?${q.toString()}`);
  return res.data;
}

export async function getTicket(projectId: string, ticketId: string): Promise<Ticket> {
  const res = await api.get<ApiTicket>(`${BASE}/${projectId}/${ticketId}`);
  return mapTicket(res.data);
}

export async function createTicket(projectId: string, data: CreateTicketRequest): Promise<Ticket> {
  const res = await api.post<ApiTicket>(`${BASE}/${projectId}`, data);
  return mapTicket(res.data);
}

export async function updateTicket(projectId: string, ticketId: string, data: UpdateTicketRequest): Promise<Ticket> {
  const res = await api.patch<ApiTicket>(`${BASE}/${projectId}/${ticketId}`, data);
  return mapTicket(res.data);
}

export async function deleteTickets(projectId: string, ticketIds: string | string[]): Promise<number> {
  const ids = Array.isArray(ticketIds) ? ticketIds : [ticketIds];
  const res = await api.delete<{ deleted: number }>(`${BASE}/${projectId}`, {
    data: { ticket_ids: ids },
  });
  return res.data.deleted;
}

export async function getCabTicketCount(projectId: string): Promise<number> {
  const res = await api.get<{ count: number }>(`${BASE}/${projectId}/cab-count`);
  return res.data.count;
}

export async function getTicketCounts(projectId: string): Promise<{ all: number; my: number }> {
  const res = await api.get<{ all: number; my: number }>(`${BASE}/${projectId}/counts`);
  return res.data;
}

export async function generateTicketsFromCAB(projectId: string): Promise<GenerateFromCABResult> {
  const res = await api.post<{ created: number; tickets: ApiTicket[] }>(
    `${BASE}/${projectId}/generate-from-cab`,
  );
  return {
    created: res.data.created,
    tickets: res.data.tickets.map(mapTicket),
  };
}
